import React from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { AttachmentPicker, AttachmentList } from "../components/Attachments";
import { MessageSquare, Send, Plus, Inbox } from "lucide-react";

function fmtDate(iso) {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        return d.toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
    } catch {
        return "";
    }
}

/**
 * Secure messaging inbox used by all three roles. Backend enforces recipient
 * eligibility so we can trust /api/messages/contacts to only return people the
 * caller may reach.
 */
export default function MessagesPage() {
    const { user } = useAuth();
    const [threads, setThreads] = React.useState([]);
    const [selected, setSelected] = React.useState(null); // partner id
    const [thread, setThread] = React.useState(null); // {partner, messages}
    const [body, setBody] = React.useState("");
    const [attachments, setAttachments] = React.useState([]);
    const [sending, setSending] = React.useState(false);
    const bottomRef = React.useRef(null);

    const loadThreads = React.useCallback(() => {
        api.get("/messages/threads").then(r => setThreads(r.data)).catch(() => { });
    }, []);

    const loadThread = React.useCallback((pid) => {
        if (!pid) { setThread(null); return; }
        api.get(`/messages/thread/${pid}`)
            .then(r => setThread(r.data))
            .catch(err => toast.error(err.response?.data?.detail || "Cannot open thread"));
    }, []);

    React.useEffect(() => { loadThreads(); }, [loadThreads]);
    React.useEffect(() => { loadThread(selected); }, [selected, loadThread]);
    React.useEffect(() => {
        // Auto-scroll to newest message when the thread loads or grows.
        bottomRef.current?.scrollIntoView({ block: "end", behavior: "smooth" });
    }, [thread?.messages?.length]);

    async function send() {
        if (!thread) return;
        if (!body.trim() && attachments.length === 0) return;
        setSending(true);
        try {
            await api.post("/messages", {
                recipient_id: thread.partner.id,
                body: body.trim() || "(attachment)",
                attachments,
            });
            setBody("");
            setAttachments([]);
            loadThread(selected);
            loadThreads();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Send failed");
        } finally {
            setSending(false);
        }
    }

    function onStartedNew(pid) {
        setSelected(pid);
        loadThreads();
    }

    return (
        <div className="grid md:grid-cols-[300px_1fr] gap-4" data-testid="messages-page">
            {/* Threads sidebar */}
            <Card className="md:sticky md:top-24 md:self-start">
                <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2 font-dash font-semibold">
                            <Inbox className="w-4 h-4 text-primary" /> Inbox
                        </div>
                        <NewMessageDialog onStarted={onStartedNew} />
                    </div>
                    <div className="space-y-1 max-h-[70vh] overflow-y-auto">
                        {threads.length === 0 && (
                            <div className="text-xs text-brand-stone italic text-center py-6">
                                No conversations yet. Start a new one →
                            </div>
                        )}
                        {threads.map(t => (
                            <button
                                key={t.partner_id}
                                onClick={() => setSelected(t.partner_id)}
                                data-testid={`thread-${t.partner_id}`}
                                className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${selected === t.partner_id ? "bg-primary/10" : "hover:bg-secondary/60"}`}
                            >
                                <div className="flex items-center justify-between">
                                    <span className="font-medium truncate">{t.partner_name}</span>
                                    {t.unread > 0 && (
                                        <Badge className="bg-primary text-primary-foreground border-0 h-5 min-w-[20px] px-1.5 text-[10px]">{t.unread}</Badge>
                                    )}
                                </div>
                                <div className="text-[11px] text-brand-stone truncate">{t.partner_role} · {t.last_body}</div>
                                <div className="text-[10px] text-brand-stone">{fmtDate(t.last_at)}</div>
                            </button>
                        ))}
                    </div>
                </CardContent>
            </Card>

            {/* Thread view */}
            <Card>
                <CardContent className="p-0 flex flex-col h-[75vh]">
                    {!thread ? (
                        <div className="flex-1 flex items-center justify-center text-brand-stone text-sm">
                            <div className="text-center">
                                <MessageSquare className="w-10 h-10 mx-auto mb-3 opacity-40" />
                                Select a conversation, or start a new one.
                            </div>
                        </div>
                    ) : (
                        <>
                            <div className="border-b border-border px-5 py-3">
                                <div className="font-dash font-semibold" data-testid="thread-partner-name">{thread.partner.full_name}</div>
                                <div className="text-xs text-brand-stone capitalize">{thread.partner.role} · {thread.partner.email}</div>
                            </div>
                            <div className="flex-1 overflow-y-auto p-5 space-y-3" data-testid="thread-messages">
                                {thread.messages.length === 0 && (
                                    <div className="text-center text-brand-stone italic text-sm py-10">
                                        No messages yet. Say hello 👋
                                    </div>
                                )}
                                {thread.messages.map(m => {
                                    const mine = m.sender_id === user.id;
                                    return (
                                        <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                                            <div
                                                className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm ${mine ? "bg-primary text-primary-foreground rounded-br-sm" : "bg-secondary rounded-bl-sm"}`}
                                                data-testid={`msg-${m.id}`}
                                            >
                                                <div className="whitespace-pre-wrap break-words">{m.body}</div>
                                                {m.attachments?.length > 0 && (
                                                    <div className={mine ? "-mx-2 mt-2" : "-mx-2 mt-2"}>
                                                        <AttachmentList items={m.attachments} testid={`msg-att-${m.id}`} />
                                                    </div>
                                                )}
                                                <div className={`text-[10px] mt-1 ${mine ? "text-primary-foreground/70" : "text-brand-stone"}`}>{fmtDate(m.created_at)}</div>
                                            </div>
                                        </div>
                                    );
                                })}
                                <div ref={bottomRef} />
                            </div>
                            <div className="border-t border-border p-4 space-y-2">
                                <AttachmentPicker value={attachments} onChange={setAttachments} testid="message-attachments" />
                                <div className="flex items-end gap-2">
                                    <Textarea
                                        value={body}
                                        onChange={e => setBody(e.target.value)}
                                        placeholder={`Write to ${thread.partner.full_name}…`}
                                        data-testid="message-input"
                                        rows={2}
                                        className="min-h-[52px] resize-none"
                                        onKeyDown={e => {
                                            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); send(); }
                                        }}
                                    />
                                    <Button
                                        onClick={send}
                                        disabled={sending || (!body.trim() && attachments.length === 0)}
                                        data-testid="send-message-btn"
                                        className="bg-primary hover:bg-primary/90 gap-2 h-[52px]"
                                    >
                                        <Send className="w-4 h-4" /> Send
                                    </Button>
                                </div>
                            </div>
                        </>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}

function NewMessageDialog({ onStarted }) {
    const [open, setOpen] = React.useState(false);
    const [contacts, setContacts] = React.useState([]);
    const [pick, setPick] = React.useState("");
    const [q, setQ] = React.useState("");

    React.useEffect(() => {
        if (open && contacts.length === 0) {
            api.get("/messages/contacts").then(r => setContacts(r.data)).catch(() => setContacts([]));
        }
    }, [open, contacts.length]);

    const filtered = contacts.filter(c =>
        !q || `${c.full_name} ${c.email} ${c.role}`.toLowerCase().includes(q.toLowerCase())
    );

    function start() {
        if (!pick) return;
        onStarted(pick);
        setOpen(false);
        setPick("");
        setQ("");
    }

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button size="sm" variant="outline" data-testid="new-message-btn" className="gap-1.5 h-8">
                    <Plus className="w-3.5 h-3.5" /> New
                </Button>
            </DialogTrigger>
            <DialogContent>
                <DialogHeader><DialogTitle className="font-serif text-2xl">New message</DialogTitle></DialogHeader>
                <div className="space-y-3">
                    <Input placeholder="Search people…" value={q} onChange={e => setQ(e.target.value)} data-testid="new-msg-search" />
                    <div className="max-h-64 overflow-y-auto border border-border rounded-lg divide-y">
                        {filtered.length === 0 && (
                            <div className="p-4 text-sm text-brand-stone text-center italic">No contacts available.</div>
                        )}
                        {filtered.map(c => (
                            <button
                                key={c.id}
                                onClick={() => setPick(c.id)}
                                data-testid={`new-msg-contact-${c.id}`}
                                className={`w-full text-left px-3 py-2 text-sm hover:bg-secondary/60 ${pick === c.id ? "bg-primary/10" : ""}`}
                            >
                                <div className="font-medium">{c.full_name}</div>
                                <div className="text-[11px] text-brand-stone capitalize">{c.role} · {c.email}</div>
                            </button>
                        ))}
                    </div>
                    <Button onClick={start} disabled={!pick} data-testid="start-thread-btn" className="w-full bg-primary hover:bg-primary/90">
                        Start conversation
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
